import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkflow } from '../contexts/WorkflowContext';
import { StepStatus, WorkflowStepInfo, WorkflowType } from '../api/athena';

const DEMOGRAPHIC_PAGE_ORDER = [
  'personal-information',
  'contact-and-communication',
  'address-and-identity',
  'emergency-and-billing',
  'care-and-insurance',
] as const;

type DemographicRouteKey = (typeof DEMOGRAPHIC_PAGE_ORDER)[number];

const NON_DEMOGRAPHIC_TYPE_ORDER: ReadonlyArray<WorkflowType> = [
  WorkflowType.HEALTH_HISTORY_FORM,
  WorkflowType.SCREENERS,
  WorkflowType.CONSENT_FORM,
];

interface Unit {
  key: string;
  isDemographic: boolean;
  routeKey?: DemographicRouteKey;
  step?: WorkflowStepInfo;
}

interface CheckinProgress {
  percent: number;
  currentIndex: number;
  total: number;
}

const extractRouteKey = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
};

const stepIsDone = (step?: WorkflowStepInfo): boolean =>
  !!step && (step.status === StepStatus.COMPLETED || step.status === StepStatus.SYNCED);

export function useCheckinProgress(): CheckinProgress {
  const { workflow } = useWorkflow();
  const location = useLocation();

  return useMemo(() => {
    const steps = workflow?.steps ?? [];

    const units: Unit[] = [];

    const demographicStep = steps.find(s => s.workflowType === WorkflowType.DEMOGRAPHIC);
    if (demographicStep) {
      for (const routeKey of DEMOGRAPHIC_PAGE_ORDER) {
        units.push({
          key: `demo:${routeKey}`,
          isDemographic: true,
          routeKey,
          step: demographicStep,
        });
      }
    }

    for (const type of NON_DEMOGRAPHIC_TYPE_ORDER) {
      const ofType = steps
        .filter(s => s.workflowType === type)
        .sort((a, b) => a.displayOrder - b.displayOrder);
      for (const step of ofType) {
        units.push({ key: `step:${step.id}`, isDemographic: false, step });
      }
    }

    const total = units.length;
    if (total === 0) {
      return { percent: 0, currentIndex: 1, total: 1 };
    }

    const currentRouteKey = extractRouteKey(location.pathname);

    let currentIndex = units.findIndex(unit => {
      if (unit.isDemographic) return unit.routeKey === currentRouteKey;
      return unit.step?.stepUrl.endsWith(`/${currentRouteKey}`) ?? false;
    });
    if (currentIndex < 0) currentIndex = 0;

    const currentUnit = units[currentIndex];
    const inDemographicPhase = !!currentUnit?.isDemographic;

    let filled = 0;
    for (let i = 0; i < units.length; i++) {
      const unit = units[i];
      if (unit.isDemographic) {
        // Demographic units fill purely by position: past sub-pages, or any
        // time the user has progressed past the entire demographic block.
        // The backend DEMOGRAPHIC row's status is intentionally ignored to
        // keep the bar honest during back-navigation and to prevent it from
        // jumping ahead if the row is flipped to COMPLETED early.
        if (!inDemographicPhase) {
          filled += 1;
        } else if (i < currentIndex) {
          filled += 1;
        }
      } else if (stepIsDone(unit.step)) {
        filled += 1;
      }
    }

    const percent = Math.round((filled / total) * 100);

    return {
      percent: Math.min(100, percent),
      currentIndex: currentIndex + 1,
      total,
    };
  }, [workflow, location.pathname]);
}

export default useCheckinProgress;
